import { Button } from '@edsn/pie-components';
import attachmentIcon from '@iconify/icons-material-symbols/attachment';
import closeIcon from '@iconify/icons-material-symbols/close';
import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import { ConditionalWrapper } from '#pie/utils/ConditionalWrapper';
import { formatBytes } from '#pie/utils/files';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';

export interface FileAnchorProps {
  className?: string;
  fileName: string;
  fileSize: number;
  onDelete?: () => void;
  onClick?: () => void;
  isLoading?: boolean;
}

export const FileAnchor: React.FC<FileAnchorProps> = ({
  className,
  onDelete,
  onClick,
  fileName,
  fileSize,
  isLoading = false,
}) => {
  const s = fileAnchor({
    isLoading: isLoading,
  });
  return (
    <div className={cn(s.base(), className)}>
      <ConditionalWrapper
        condition={!!onClick || isLoading}
        wrapper={(children) => (
          <button
            className={s.button()}
            type="button"
            onClick={onClick}
            aria-busy={isLoading}
            disabled={isLoading}
          >
            {children}
          </button>
        )}
      >
        <Icon
          icon={attachmentIcon}
          size={24}
          className="text-action-secondary-enabled flex items-center"
        />
        <span>{fileName}</span>
        <span className="text-content-tertiary">({formatBytes(fileSize)})</span>
        {isLoading && (
          <Icon
            role="progressbar"
            aria-label="loading"
            icon={spinnerIcon}
            size={16}
            className={s.spinner()}
          />
        )}
      </ConditionalWrapper>

      {onDelete && (
        <Button
          aria-label="Delete file"
          iconStart={
            <Icon icon={closeIcon} size={16} className="text-content-error" />
          }
          action="tertiary"
          emphasis="minimal"
          className="p-0"
          onClick={(e) => {
            e.preventDefault();
            onDelete?.();
          }}
        />
      )}
    </div>
  );
};

const fileAnchor = tv({
  base: 'flex w-full items-center gap-1',
  slots: {
    button: 'flex items-center gap-1 text-action-secondary-enabled',
    icon: 'text-action-secondary-enabled',
    spinner: 'animate-spin',
  },
  variants: {
    isLoading: {
      true: {
        base: 'opacity-50',
        button: 'text-content-tertiary',
      },
    },
  },
});
